// src/features/sidebar-store/sidebarStore.ts
// implements: CRS-V21-SBF-003

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { SidebarContext, ContextTabBadge } from '@/shared/types';

export interface SidebarStore {
  // ── State ──
  activeContext: SidebarContext;
  activeRoleId: string | null;
  secondaryRoleId: string | null;
  collapsed: boolean;
  sectionStates: Record<SidebarContext, Record<string, boolean>>;
  contextBadges: Record<SidebarContext, ContextTabBadge | null>;
  featureFlags: Record<string, boolean>;
  sseConnected: boolean;

  // ── Actions ──
  setActiveContext: (ctx: SidebarContext) => void;
  setActiveRole: (roleId: string | null) => void;
  setSecondaryRole: (roleId: string | null) => void;
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
  toggleSection: (ctx: SidebarContext, sectionId: string) => void;
  setSectionCollapsed: (ctx: SidebarContext, sectionId: string, collapsed: boolean) => void;
  setContextBadge: (ctx: SidebarContext, badge: ContextTabBadge | null) => void;
  incrementContextBadge: (ctx: SidebarContext) => void;
  clearContextBadge: (ctx: SidebarContext) => void;
  setFeatureFlags: (flags: Record<string, boolean>) => void;
  setSseConnected: (connected: boolean) => void;
  reset: () => void;
}

const INITIAL_SECTION_STATES: Record<SidebarContext, Record<string, boolean>> = {
  personal: {},
  team: {},
  company: {},
  role: {},
};

const memoryMap = new Map<string, string>();

const safeStorage = createJSONStorage(() => {
  if (
    typeof window !== 'undefined' &&
    window.localStorage &&
    typeof window.localStorage.getItem === 'function'
  ) {
    return window.localStorage;
  }
  return {
    getItem: (key: string) => memoryMap.get(key) ?? null,
    setItem: (key: string, value: string) => memoryMap.set(key, value),
    removeItem: (key: string) => memoryMap.delete(key),
  };
});

export const useSidebarStore = create<SidebarStore>()(
  persist(
    (set) => ({
      activeContext: 'personal',
      activeRoleId: null,
      secondaryRoleId: null,
      collapsed: false,
      sectionStates: INITIAL_SECTION_STATES,
      contextBadges: { personal: null, team: null, company: null, role: null },
      featureFlags: {},
      sseConnected: false,

      setActiveContext: (ctx) => set({ activeContext: ctx }),
      setActiveRole: (roleId) => set({ activeRoleId: roleId }),
      setSecondaryRole: (roleId) => set({ secondaryRoleId: roleId }),
      setCollapsed: (collapsed) => set({ collapsed }),
      toggleCollapsed: () => set((s) => ({ collapsed: !s.collapsed })),

      toggleSection: (ctx, sectionId) =>
        set((s) => ({
          sectionStates: {
            ...s.sectionStates,
            [ctx]: {
              ...s.sectionStates[ctx],
              [sectionId]: !(s.sectionStates[ctx]?.[sectionId] ?? false),
            },
          },
        })),

      setSectionCollapsed: (ctx, sectionId, collapsed) =>
        set((s) => ({
          sectionStates: {
            ...s.sectionStates,
            [ctx]: { ...s.sectionStates[ctx], [sectionId]: collapsed },
          },
        })),

      setContextBadge: (ctx, badge) =>
        set((s) => ({ contextBadges: { ...s.contextBadges, [ctx]: badge } })),

      incrementContextBadge: (ctx) =>
        set((s) => {
          const curr = s.contextBadges[ctx];
          return {
            contextBadges: {
              ...s.contextBadges,
              [ctx]: curr
                ? { ...curr, count: curr.count + 1 }
                : { count: 1, type: 'numeric' as const, color: 'accent' as const },
            },
          };
        }),

      clearContextBadge: (ctx) =>
        set((s) => ({ contextBadges: { ...s.contextBadges, [ctx]: null } })),

      setFeatureFlags: (flags) => set({ featureFlags: flags }),
      setSseConnected: (connected) => set({ sseConnected: connected }),

      reset: () =>
        set({
          activeContext: 'personal',
          activeRoleId: null,
          secondaryRoleId: null,
          collapsed: false,
          sectionStates: INITIAL_SECTION_STATES,
          contextBadges: { personal: null, team: null, company: null, role: null },
          featureFlags: {},
          sseConnected: false,
        }),
    }),
    {
      name: 'sidebar-store-v21',
      storage: safeStorage,
      partialize: (s) => ({
        activeContext: s.activeContext,
        activeRoleId: s.activeRoleId,
        secondaryRoleId: s.secondaryRoleId,
        collapsed: s.collapsed,
        sectionStates: s.sectionStates,
      }),
    }
  )
);
